import React, { useEffect, useRef, useState } from "react";
import AppSnackbar from "components/common/snackbar/AppSnackbar";
import {
  Avatar,
  Box,
  Card,
  CardContent,
  CircularProgress,
  Grid,
  Typography,
} from "@mui/material";
import PhotoCameraRoundedIcon from "@mui/icons-material/PhotoCameraRounded";
import SaveRoundedIcon from "@mui/icons-material/SaveRounded";
import UploadRoundedIcon from "@mui/icons-material/UploadRounded";
import { AppButton } from "components/common/buttons";
import { AppTextField } from "components/common/inputs";
import { useAuth } from "context/AuthContext";
import { updateUser, uploadAvatar } from "modules/systems/services/user.service";
import { getFullAvatarUrl, getInitials } from "utils/imageUtils";

const sectionCardSx = {
  border: "1px solid #e3e9ef",
  borderRadius: "12px",
  boxShadow: "0 1px 2px rgba(16, 24, 40, 0.03)",
  backgroundColor: "#fff",
};

const sectionContentSx = {
  p: { xs: 2.5, sm: 3 },
  "&:last-child": { pb: { xs: 2.5, sm: 3 } },
};

const PersonalInfoTab = ({ user, onUserUpdate }) => {
  const { updateUserContext } = useAuth();
  const fileInputRef = useRef(null);
  const [formData, setFormData] = useState({
    fullName: user?.fullName || "",
    email: user?.email || "",
  });
  const [loading, setLoading] = useState(false);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  useEffect(() => {
    setFormData({
      fullName: user?.fullName || "",
      email: user?.email || "",
    });
  }, [user?.fullName, user?.email]);

  const handleInputChange = (field) => (value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSaveProfile = async () => {
    try {
      setLoading(true);

      if (!formData.fullName.trim()) {
        setSnackbar({
          open: true,
          message: "Họ và tên không được để trống",
          severity: "error",
        });
        return;
      }

      if (
        formData.email &&
        !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)
      ) {
        setSnackbar({
          open: true,
          message: "Email không hợp lệ",
          severity: "error",
        });
        return;
      }

      await updateUser(user.userId, {
        fullName: formData.fullName.trim(),
        email: formData.email.trim() || null,
      });

      if (updateUserContext) {
        updateUserContext({
          ...user,
          fullName: formData.fullName.trim(),
          email: formData.email.trim(),
        });
      }

      setSnackbar({
        open: true,
        message: "Cập nhật thông tin thành công",
        severity: "success",
      });

      onUserUpdate?.();
    } catch (error) {
      console.error("Error updating profile:", error);
      setSnackbar({
        open: true,
        message: "Có lỗi xảy ra khi cập nhật thông tin",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleAvatarUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    try {
      setUploadingAvatar(true);

      const allowedTypes = [
        "image/jpeg",
        "image/jpg",
        "image/png",
        "image/gif",
      ];
      if (!allowedTypes.includes(file.type)) {
        setSnackbar({
          open: true,
          message: "Chỉ chấp nhận file ảnh JPG, PNG hoặc GIF",
          severity: "error",
        });
        return;
      }

      const maxSize = 5 * 1024 * 1024;
      if (file.size > maxSize) {
        setSnackbar({
          open: true,
          message: "Kích thước file không được vượt quá 5MB",
          severity: "error",
        });
        return;
      }

      const avatarFormData = new FormData();
      avatarFormData.append("file", file);
      const response = await uploadAvatar(user.userId, avatarFormData);

      if (
        response?.success &&
        response?.data?.avatarFilePath &&
        updateUserContext
      ) {
        updateUserContext({
          ...user,
          avatarFilePath: response.data.avatarFilePath,
        });
      }

      setSnackbar({
        open: true,
        message: "Cập nhật ảnh đại diện thành công",
        severity: "success",
      });

      onUserUpdate?.();
    } catch (error) {
      console.error("Error uploading avatar:", error);
      setSnackbar({
        open: true,
        message: "Có lỗi xảy ra khi tải lên ảnh đại diện",
        severity: "error",
      });
    } finally {
      setUploadingAvatar(false);
      event.target.value = "";
    }
  };

  const closeSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  return (
    <Box sx={{ display: "grid", gap: 2.5 }}>
      <Card variant="outlined" sx={sectionCardSx}>
        <CardContent sx={sectionContentSx}>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 2.5 }}>
            Ảnh đại diện
          </Typography>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: { xs: 2, sm: 3 },
              flexWrap: { xs: "wrap", sm: "nowrap" },
            }}
          >
            <Box sx={{ position: "relative", flex: "0 0 auto" }}>
              <Avatar
                src={getFullAvatarUrl(user?.avatarFilePath)}
                alt={user?.fullName || user?.userName}
                sx={{ width: 88, height: 88, fontSize: 32, fontWeight: 600 }}
                imgProps={{
                  onError: (event) => {
                    event.target.onerror = null;
                    event.target.style.display = "none";
                  },
                  referrerPolicy: "origin",
                  crossOrigin: "anonymous",
                }}
              >
                {getInitials(user?.fullName || user?.userName)}
              </Avatar>
              <Box
                sx={{
                  position: "absolute",
                  right: -3,
                  bottom: 1,
                  width: 30,
                  height: 30,
                  borderRadius: "50%",
                  display: "grid",
                  placeItems: "center",
                  color: "#fff",
                  backgroundColor: "primary.main",
                  border: "3px solid #fff",
                }}
              >
                <PhotoCameraRoundedIcon sx={{ fontSize: 16 }} />
              </Box>
            </Box>

            <Box sx={{ minWidth: 0 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 650 }}>
                Thay đổi ảnh đại diện
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25, mb: 1.25 }}>
                Chấp nhận JPG, PNG hoặc GIF. Tối đa 5MB.
              </Typography>

              <input
                ref={fileInputRef}
                accept="image/jpeg,image/png,image/gif"
                hidden
                id="avatar-upload"
                type="file"
                onChange={handleAvatarUpload}
                disabled={uploadingAvatar}
              />
              <AppButton
                variant="neutral"
                component="label"
                htmlFor="avatar-upload"
                startIcon={
                  uploadingAvatar ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : (
                    <UploadRoundedIcon />
                  )
                }
                disabled={uploadingAvatar}
              >
                {uploadingAvatar ? "Đang tải lên..." : "Tải ảnh mới"}
              </AppButton>
            </Box>
          </Box>
        </CardContent>
      </Card>

      <Card variant="outlined" sx={sectionCardSx}>
        <CardContent sx={sectionContentSx}>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 2.5 }}>
            Thông tin cá nhân
          </Typography>

          <Grid container spacing={2.5}>
            <Grid size={{ xs: 12, md: 6 }}>
              <AppTextField
                label="Họ và tên"
                value={formData.fullName}
                onChange={handleInputChange("fullName")}
                required
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <AppTextField
                label="Email"
                type="email"
                value={formData.email}
                onChange={handleInputChange("email")}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <AppTextField
                label="Tên đăng nhập"
                value={user?.userName || ""}
                disabled
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <AppTextField
                label="Vai trò"
                value={user?.roleName || user?.role || "Chưa xác định"}
                disabled
              />
            </Grid>
          </Grid>

          <Box display="flex" justifyContent="flex-end" sx={{ mt: 3 }}>
            <AppButton
              variant="contained"
              startIcon={
                loading ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  <SaveRoundedIcon />
                )
              }
              onClick={handleSaveProfile}
              disabled={loading}
            >
              {loading ? "Đang lưu..." : "Lưu thay đổi"}
            </AppButton>
          </Box>
        </CardContent>
      </Card>

      <AppSnackbar
        open={snackbar.open}
        onClose={closeSnackbar}
        severity={snackbar.severity}
        message={snackbar.message}
      />
    </Box>
  );
};

export default PersonalInfoTab;
